import { type StateCreator } from "zustand";

import { onboardingService, type TOnboardingSummary } from "@/api/onboarding";
import { isCacheStale } from "@/utils/cacheUtils";
import {
  getSessionExpiredAwareErrorMessage,
  isSessionExpiredHttpError,
  SESSION_EXPIRED_USER_MESSAGE,
} from "@/utils/authUtils";

type TOnboardingSummaryCacheEntry = {
  data: TOnboardingSummary;
  lastFetched: number;
};

export type TOnboardingStoreState = {
  summaryByCompanyId: Record<string, TOnboardingSummaryCacheEntry>;
  loadingCompanyIds: Set<string>;
  errors: Record<string, string | null>;
  onboardingCacheTTL: number; // TTL in milliseconds (default: 5 minutes)
};

export type TOnboardingStoreActions = {
  fetchOnboardingSummary: (companyId: string, options?: { force?: boolean; lite?: boolean }) => Promise<TOnboardingSummary | null>;
  getOnboardingSummary: (companyId: string) => TOnboardingSummary | null;
  invalidateOnboardingSummary: (companyId: string) => void;
  invalidateAllOnboardingSummaries: () => void;
};

export type TOnboardingStore = TOnboardingStoreState & TOnboardingStoreActions;

const DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const initialOnboardingStoreState: TOnboardingStoreState = {
  summaryByCompanyId: {},
  loadingCompanyIds: new Set(),
  errors: {},
  onboardingCacheTTL: DEFAULT_CACHE_TTL,
};


export const createOnboardingSlice: StateCreator<TOnboardingStore> = (set, get) => ({
  ...initialOnboardingStoreState,

  fetchOnboardingSummary: async (companyId: string, options = {}) => {
    const { force = false, lite = false } = options;
    const state = get();

    // Ensure loadingCompanyIds is always a Set
    const loadingCompanyIds = state.loadingCompanyIds instanceof Set ? state.loadingCompanyIds : new Set<string>();

    // Check if we should skip fetch (cache is valid and not forcing)
    const cached = state.summaryByCompanyId[companyId];
    if (!force && cached && !isCacheStale(cached.lastFetched, state.onboardingCacheTTL)) {
      return cached.data;
    }

    // Prevent concurrent fetches for the same company
    if (loadingCompanyIds.has(companyId)) {
      // Return cached data if available while loading
      return cached?.data ?? null;
    }

    // Mark company as loading
    set({
      loadingCompanyIds: new Set([...loadingCompanyIds, companyId]),
      errors: { ...state.errors, [companyId]: null },
    });

    try {
      const { data, error } = await onboardingService.getOnboardingSummary(companyId, undefined, lite);

      if (error) {
        const errorMessage = isSessionExpiredHttpError(error)
          ? SESSION_EXPIRED_USER_MESSAGE
          : await getSessionExpiredAwareErrorMessage(error, "Failed to load onboarding summary");
        set((prev) => {
          const prevLoadingIds = prev.loadingCompanyIds instanceof Set ? prev.loadingCompanyIds : new Set<string>();
          return {
            loadingCompanyIds: new Set([...prevLoadingIds].filter((id) => id !== companyId)),
            errors: { ...prev.errors, [companyId]: errorMessage },
          };
        });
        return null;
      }

      if (data?.data) {
        const summary = data.data;
        // Lite polls are status-only — never cache them, or a later full read
        // could be served a payload missing `summary`/files from the cache.
        if (!lite) {
          set((prev) => {
            const prevLoadingIds = prev.loadingCompanyIds instanceof Set ? prev.loadingCompanyIds : new Set<string>();
            return {
              summaryByCompanyId: {
                ...prev.summaryByCompanyId,
                [companyId]: {
                  data: summary,
                  lastFetched: Date.now(),
                },
              },
              loadingCompanyIds: new Set([...prevLoadingIds].filter((id) => id !== companyId)),
              errors: { ...prev.errors, [companyId]: null },
            };
          });
        } else {
          set((prev) => {
            const prevLoadingIds = prev.loadingCompanyIds instanceof Set ? prev.loadingCompanyIds : new Set<string>();
            return {
              loadingCompanyIds: new Set([...prevLoadingIds].filter((id) => id !== companyId)),
            };
          });
        }
        return summary;
      }

      return null;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load onboarding summary";
      set((prev) => {
        const prevLoadingIds = prev.loadingCompanyIds instanceof Set ? prev.loadingCompanyIds : new Set<string>();
        return {
          loadingCompanyIds: new Set([...prevLoadingIds].filter((id) => id !== companyId)),
          errors: { ...prev.errors, [companyId]: errorMessage },
        };
      });
      return null;
    }
  },

  getOnboardingSummary: (companyId: string) => {
    const state = get();
    const cached = state.summaryByCompanyId[companyId];

    if (!cached) {
      // Trigger fetch if no data
      void get().fetchOnboardingSummary(companyId);
      return null;
    }

    // Check if cache is stale and trigger refresh in background
    if (isCacheStale(cached.lastFetched, state.onboardingCacheTTL)) {
      void get().fetchOnboardingSummary(companyId);
    }

    return cached.data;
  },

  invalidateOnboardingSummary: (companyId: string) => {
    set((prev) => {
      const newSummaryByCompanyId = { ...prev.summaryByCompanyId };
      delete newSummaryByCompanyId[companyId];
      const newErrors = { ...prev.errors };
      delete newErrors[companyId];
      return {
        summaryByCompanyId: newSummaryByCompanyId,
        errors: newErrors,
      };
    });
  },

  invalidateAllOnboardingSummaries: () => {
    set({
      summaryByCompanyId: {},
      errors: {},
      loadingCompanyIds: new Set(),
    });
  },
});
